<template>
    <vx-table-row class="relative pl-12">
        <vx-table-row-title>
            <div class="w-2 sm:w-3 min-h-[30px] mr-3 rounded-full absolute inset-s-4 inset-bs-4 inset-be-4 md:inset-s-6" :class="status.color"></div>

            <div class="text-sm font-normal">
                {{ status.label }}
            </div>
        </vx-table-row-title>
        <vx-table-row-item>
            <template v-slot:label> Completed: </template>

            <vx-popover hover :arrow="true" placement="top" :key="createdAtKey">
                {{ fromNow(props.item.created_at) }}
                <template v-slot:content>
                    {{ shortDateTime(props.item.created_at) }}
                </template>
            </vx-popover>

        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Duration: </template>

            {{ formatDuration(props.item.duration_ms) }}

        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Attempt ID: </template>

            <vx-popover hover :arrow="true" placement="top">
                <vx-badge size="xs" color="slate">{{ props.item.attempt_id?.slice(-6) }}</vx-badge>
                <template v-slot:content>
                    {{ props.item.attempt_id }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button v-if="isResponseSupported" color="muted-light" size="sm" class="col-span-12" data-test="show-response-btn"
                @click="showResponse = !showResponse">
                Response
                <font-awesome-icon widthAuto :icon="showResponse ? faCaretUp : faCaretDown" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>

    <div class="pl-12 md:pl-16 relative col-span-full py-1">
        <div
            v-if="nextItem || showResponse"
            class=" w-px absolute inset-bs-0 inset-s-5 ml-px sm:ml-0 sm:inset-s-6 md:inset-s-8"
            :class="[
                !nextItem || status.code === nextItemStatus?.code || !showResponse ? status.lineColor : status.gradient,
                {
                    'h-full': nextItem,
                    'h-10': !nextItem,
                    'opacity-0': !nextItem && !showResponse,
                    'transition': !nextItem
                }
            ]"
        ></div>

        <div class="absolute w-5 h-5 inset-s-[11px] inset-bs-10 rounded-full border-4 border-solid border-slate-200 md:inset-s-[22px] sm:inset-s-[14px] transition" :class="[status.lineColor, {'opacity-0': !nextItem && !showResponse}]"></div>

        <animate-height :height="showResponse ? 'auto' : 0" :animateOpacity="true">
            <api-delivery-response-card :response="response" class="my-2"></api-delivery-response-card>
        </animate-height>
    </div>
</template>

<script setup>
import {
    faCaretDown,
    faCaretUp
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxTableRow,
    VxTableRowItem,
    VxTableRowButtons,
    VxBadge,
    VxPopover,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import { fromNow, shortDateTime, formatDuration } from '../../../../../../../utils/date';
import { computed, ref, watch } from "vue";
import { customDeliveriesClient } from '~/services';
import ApiDeliveryResponseCard from "../cards/ApiDeliveryResponseCard.vue";
import AnimateHeight from '~/components/general/AnimateHeight.vue';

const statuses = [
    {
        color: 'bg-sky-200',
        lineColor: 'bg-sky-400',
        gradient: 'bg-linear-to-b from-sky-400 from-40% via-indigo-400 to-rose-400',
        label: 'Success',
        code: null
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Redirection Error',
        code: 'redirection_error'
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Too Many Requests Error',
        code: 'too_many_requests_error'
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Client Error',
        code: 'client_error'
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Server Error',
        code: 'server_error'
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Network Error',
        code: 'network_error'
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Timeout Error',
        code: 'timeout_error'
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Auth Config Error',
        code: 'auth_config_error'
    },
    {
        color: 'bg-rose-400',
        lineColor: 'bg-rose-400',
        gradient: 'bg-linear-to-b from-rose-400 from-40% via-indigo-400 to-sky-400',
        label: 'Internal Error',
        code: 'internal_error'
    },
];

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    nextItem: {
        type: Object,
        required: false,
    }
});

const status = computed(() => statuses.find((s) => s.code === props.item.reason_code) || {
    color: 'bg-slate-200',
    lineColor: 'bg-slate-400',
    gradient: 'bg-slate-400',
    label: 'Unknown',
    code: props.item?.reason_code || `unknown`
});

const nextItemStatus = computed(() => statuses.find((s) => s.code === props.nextItem?.reason_code) || null);

const createdAtKey = useInterval(60000);

const isResponseSupported = computed(() => [null, 'redirection_error', 'client_error', 'server_error'].includes(props.item.reason_code));
const showResponse = ref(false);
const response = ref(undefined);
const responseLoading = ref(false);

const loadResponse = async () => {
    if (responseLoading.value || !isResponseSupported.value) {
        return;
    }

    responseLoading.value = true;
    try {
        response.value = (await customDeliveriesClient.getAttemptResponse(
            props.item.team_id,
            props.item.origin_type,
            props.item.origin_id,
            props.item.delivery_id,
            props.item.attempt_id
        )).data;
    } catch (error) {
        console.error(error);
        response.value = null;
    } finally {
        responseLoading.value = false;
    }
}

watch(() => showResponse.value, (newVal) => {
    if (newVal && response.value === undefined) {
        // delay or it will a layout shift will happen as the response card is animated in
        setTimeout(() => {
            loadResponse();
        }, 300);
    }
});

</script>
